import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

import {
  rememberUsageMeasurement,
  usageMeasurementIsAllowed,
} from '../domains/app/domains/the-usage-measurement-switch';

/**
 * The one place that tells Firebase whether this device agreed to be counted.
 *
 * WHAT A REFUSAL ACTUALLY STOPS, AND WHEN. Two different things happen, and
 * saying so matters because the screen makes a promise to a member:
 *
 * - OUR OWN EVENTS stop at once. AnalyticsService asks this service before
 *   every send, so activity_view, searches, joins and payments stop being
 *   reported the moment the switch moves. That part is entirely ours.
 * - FIREBASE'S AUTOMATIC COLLECTION (first_open, session_start, screen_view)
 *   stops through `setEnabled`, and the plugin documents that « the value does
 *   not apply until the next run of the app ». NOT VERIFIED ON A DEVICE: that
 *   sentence is the plugin's own, version 6.3.1, and nothing here has measured
 *   it. The settings text is written to that claim rather than to a better one,
 *   which is the conservative direction: if it turns out to be immediate, the
 *   member was told something pessimistic instead of something false.
 *
 * `resetAnalyticsData` is called alongside it so what the SDK had already
 * stored on the device goes, and the instance id is reset. It is Android and
 * iOS only, so the web build keeps whatever the browser holds; that is why no
 * screen promises an erasure.
 *
 * EVERY CALL IS SWALLOWED, like AnalyticsService next door: a measurement
 * failure must never be able to break a member's flow. The failure that would
 * matter, a refusal that does not reach the SDK, is still visible in the
 * console, and the refusal is written down locally either way, so the next
 * start applies it.
 *
 * THE ACCOUNT HOLDS THE CHOICE since 15 October 2026, and talking to it is
 * UsageMeasurementAccountService's job, not this one's. Kept apart on purpose:
 * AnalyticsService asks this service before every event and is injected
 * almost everywhere, and an HttpClient here would follow it into every one of
 * those places.
 */
@Injectable({ providedIn: 'root' })
export class UsageMeasurementService {
  /** Read by AnalyticsService before every send. Synchronous on purpose. */
  isAllowed(): boolean {
    return usageMeasurementIsAllowed();
  }

  /**
   * Called once at start-up, which is what makes a refusal persist: the SDK
   * remembers `setEnabled` across runs by itself, but a build that never calls
   * it leaves a refused device collecting for ever.
   */
  async applyTheStoredChoice(): Promise<void> {
    await this.tellFirebase(usageMeasurementIsAllowed());
  }

  /** The switch moved, here or on the account: written down, then obeyed. */
  async remember(allowed: boolean, heldByTheAccount = false): Promise<void> {
    rememberUsageMeasurement(allowed, localStorage, heldByTheAccount);
    await this.tellFirebase(allowed);
  }

  /**
   * The account confirmed a refusal this device already obeys. Only the mark
   * changes: telling Firebase again would reset the instance id for nothing.
   */
  noteTheAccountHoldsTheRefusal(): void {
    if (!usageMeasurementIsAllowed()) {
      rememberUsageMeasurement(false, localStorage, true);
    }
  }

  /**
   * Somebody signed in: Firebase is told which account this is, unless the
   * device refused the measurement.
   *
   * Found on 16 October 2026 (Asana 1219575473860443): the sign-in handler
   * called `setUserId` itself, without asking, so a refused device still
   * carried its account id in the analytics SDK. Nothing was sent while
   * collection was off, but a refusal that leaves our identifier on the
   * measurement tool is not the refusal the settings screen describes. The
   * id is given back at the next sign-in after somebody allows it again.
   */
  async tellWhoThisIs(userId: string): Promise<void> {
    if (!this.isAllowed()) {
      return;
    }
    try {
      await FirebaseAnalytics.setUserId({ userId });
    } catch (error: unknown) {
      console.warn('[Analytics] the user id was not set on sign-in', error);
    }
  }

  /**
   * Somebody signed out.
   *
   * Found on 25 September 2026: the disconnect handler revoked the push token,
   * signed Firebase out and purged the stored state, and never touched the
   * analytics identity. On a shared phone the next person's events were
   * attributed to the account that left.
   */
  async forgetWhoThisWas(): Promise<void> {
    try {
      await FirebaseAnalytics.setUserId({ userId: null });
    } catch (error: unknown) {
      console.warn('[Analytics] the user id was not cleared on sign-out', error);
    }
  }

  private async tellFirebase(allowed: boolean): Promise<void> {
    try {
      await FirebaseAnalytics.setEnabled({ enabled: allowed });
    } catch (error: unknown) {
      console.warn(`[Analytics] collection was not set to ${allowed}`, error);
    }

    if (allowed) {
      return;
    }

    // The account id goes with the refusal, not only at sign-out.
    await this.forgetWhoThisWas();

    try {
      await FirebaseAnalytics.resetAnalyticsData();
    } catch (error: unknown) {
      // Android and iOS only. On the web build there is nothing to call, and
      // nothing was promised.
      console.warn('[Analytics] the data held on this device was not cleared', error);
    }
  }
}
